import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useOrganizationService } from '../../../services';
import { useJwt } from '../../../helpers/common';
import ToggleSwitch from '../../../components/common/ToggleSwitch';
import CustomButton from '../../../components/common/CustomButton';
import ConfirmDeleteModal from '../../../components/Modals/ConfirmDeleteModal';
import { Badge, Checkbox, Label, Tooltip, useThemeMode, Alert } from "flowbite-react";
import toast from "react-hot-toast";
import { HiExclamation } from 'react-icons/hi';
import OrgPreferencesModal from '../TrainAI/OrgPreferencesModal';
import { useOrganization } from "@clerk/clerk-react";
import SingleSelect from '../../../components/common/Search/SingleSelect';
import UnsavedChangesIndicator from '../../../components/common/UnsavedChangesIndicator';
import CustomTooltip from '../../../components/common/CustomTooltip';
import { HiInformationCircle, HiEmojiHappy, HiCheckCircle, HiLockClosed } from 'react-icons/hi';
import useIntegrationStatus from '../../../hooks/useIntegrationStatus';
import PriorityList, { PriorityLevel } from '../../../features/PriorityLevels/PriorityLevelsList/PriorityList';
import CacheResetAlert from '../../../features/CacheResetAlert/CacheResetAlert';

const OrgSettings = () => {
  const JWTdata: any = useJwt();
  const navigate = useNavigate();
  const org_id = JWTdata?.org_id;
  const {
    fetchOrganization,
    updateOrganization,
    getOrgPreferences,
    deleteAllOrgPreferences,
    getAllTolerance
  } = useOrganizationService();
  const { organization } = useOrganization();
  const orgImageUrl = (organization as any)?.imageUrl;

  const {
    orgDetails = {},
    onboardSettings = {},
    orgWorkingDays = [],
    loading,
    orgUpdateLoading = false,
    orgPreferencesLoading,
    orgPreferences,
    toleranceModes = [],
    toleranceModesLoading = false
  } = useSelector((state: any) => state.organization);

  console.log("currentSelectedTolreance", orgDetails.tolerance);
  const [workingDays, setWorkingDays] = useState<number>(5);
  const [hoursPerDay, setHoursPerDay] = useState<number>(8);
  const [onboardToggle, setOnboardToggle] = useState<boolean>(false);
  const [showCostToggle, setShowCostToggle] = useState<boolean>(false);
  const [storyPoints, setStoryPoints] = useState<number | string>(1);
  const [originalEstimate, setOriginalEstimate] = useState<number | string>(8);
  const [selectedToleranceId, setSelectedToleranceId] = useState<number | null>(null);
  const [toleranceIndicatorStyle, setToleranceIndicatorStyle] = useState({ width: 0, left: 0 });
  const toleranceButtonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});

  const [preferencesModalToggle, setPreferencesModalToggle] = useState(false);
  const [clearConfirmModalShow, setClearConfirmModal] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [initialValues, setInitialValues] = useState<{
    workingDays: number;
    hoursPerDay: number;
    onboardToggle: boolean;
    showCostToggle: boolean;
    storyPoints: number;
    originalEstimate: number;
    selectedToleranceId: number | null;
    priorityLevels: PriorityLevel[];
  } | null>(null);
  const [showStoryPointsResyncMessage, setShowStoryPointsResyncMessage] = useState(false);
  const [showOriginalEstimateResyncMessage, setShowOriginalEstimateResyncMessage] = useState(false);
  const [showToleranceResyncMessage, setShowToleranceResyncMessage] = useState(false);
  const [priorityLevels, setPriorityLevels] = useState<PriorityLevel[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Check integration status
  const { isIntegrationActive } = useIntegrationStatus();
  const isAzureActive = isIntegrationActive('azure');
  const isJiraActive = isIntegrationActive('jira');

  useEffect(() => {
    if (org_id) {
      fetchOrganization(org_id);
      getAllTolerance();
    }
  }, [org_id]);

  const backendWorkingDaysLength = useMemo(() => {
    if (Array.isArray(orgWorkingDays) && orgWorkingDays.length) return orgWorkingDays.length;
    if (Array.isArray(orgDetails?.working_days) && orgDetails.working_days.length) return orgDetails.working_days.length;
    return null;
  }, [orgWorkingDays, orgDetails?.working_days]);

  const backendHoursPerDay = useMemo(() => {
    return typeof orgDetails?.hours_per_day === 'number' ? orgDetails.hours_per_day : null;
  }, [orgDetails]);

  const backendOnboardToggle = useMemo(() => {
    if (onboardSettings && typeof onboardSettings.close === 'boolean') return !onboardSettings.close;
    return null;
  }, [onboardSettings]);

  const backendShowCost = useMemo(() => {
    if (typeof orgDetails?.show_cost === 'boolean') return orgDetails.show_cost;
    return null;
  }, [orgDetails]);

  const backendStoryPoints = useMemo(() => {
    if (typeof orgDetails?.story_points === 'number') return orgDetails.story_points;
    return null;
  }, [orgDetails]);

  const backendOriginalEstimate = useMemo(() => {
    if (typeof orgDetails?.original_estimate === 'number') return orgDetails.original_estimate;
    return null;
  }, [orgDetails]);

  const backendPriorityLevels = useMemo(() => {
    return Array.isArray(orgDetails?.priorities) ? orgDetails.priorities : Array.isArray(orgDetails?.priority_levels) ? orgDetails.priority_levels : null;
  }, [orgDetails]);

  const backendToleranceId = useMemo(() => {
    return typeof orgDetails?.tolerance === 'number' ? orgDetails.tolerance : null;
  }, [orgDetails]);

  useEffect(() => {
    if (
      backendWorkingDaysLength === null &&
      backendHoursPerDay === null &&
      backendOnboardToggle === null &&
      backendShowCost === null &&
      backendStoryPoints === null &&
      backendOriginalEstimate === null &&
      backendPriorityLevels === null &&
      backendToleranceId === null
    ) {
      return;
    }

    const nextValues = {
      workingDays: backendWorkingDaysLength ?? 5,
      hoursPerDay: backendHoursPerDay ?? 8,
      onboardToggle: backendOnboardToggle ?? false,
      showCostToggle: backendShowCost ?? false,
      storyPoints: backendStoryPoints ?? 1,
      originalEstimate: backendOriginalEstimate ?? 8,
      selectedToleranceId: backendToleranceId,
      priorityLevels: (backendPriorityLevels ?? []).map((l: PriorityLevel, i: number) => ({
        ...l,
        order: i + 1
      }))
    };

    setWorkingDays((prev) => (prev === nextValues.workingDays ? prev : nextValues.workingDays));
    setHoursPerDay((prev) => (prev === nextValues.hoursPerDay ? prev : nextValues.hoursPerDay));
    setOnboardToggle((prev) => (prev === nextValues.onboardToggle ? prev : nextValues.onboardToggle));
    setShowCostToggle((prev) => (prev === nextValues.showCostToggle ? prev : nextValues.showCostToggle));
    setStoryPoints((prev) => (prev === nextValues.storyPoints ? prev : nextValues.storyPoints));
    setOriginalEstimate((prev) => (prev === nextValues.originalEstimate ? prev : nextValues.originalEstimate));
    setSelectedToleranceId((prev) => (prev === nextValues.selectedToleranceId ? prev : nextValues.selectedToleranceId));
    setPriorityLevels((prev) => {
      const currentIds = prev.map((l: PriorityLevel) => l.id + l.color + l.order).join(',');
      const nextIds = nextValues.priorityLevels.map((l: PriorityLevel) => l.id + l.color + l.order).join(',');
      return currentIds === nextIds ? prev : nextValues.priorityLevels;
    });
    setInitialValues((prev) => {
      if (
        prev &&
        prev.workingDays === nextValues.workingDays &&
        prev.hoursPerDay === nextValues.hoursPerDay &&
        prev.onboardToggle === nextValues.onboardToggle &&
        prev.showCostToggle === nextValues.showCostToggle &&
        prev.storyPoints === nextValues.storyPoints &&
        prev.originalEstimate === nextValues.originalEstimate &&
        prev.selectedToleranceId === nextValues.selectedToleranceId &&
        JSON.stringify(prev.priorityLevels) === JSON.stringify(nextValues.priorityLevels)
      ) {
        return prev;
      }
      return nextValues;
    });
  }, [backendWorkingDaysLength, backendHoursPerDay, backendOnboardToggle, backendShowCost, backendStoryPoints, backendOriginalEstimate, backendPriorityLevels, backendToleranceId]);

  const createdAtText = useMemo(() => {
    const d = orgDetails?.created_at ? new Date(orgDetails.created_at) : null;
    return d ? d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
  }, [orgDetails]);

  const workingDaysOptions = useMemo(() => ([
    { label: '5 Working Days', value: 5 },
    { label: '6 Working Days', value: 6 }
  ]), []);

  const hoursOptions = useMemo(() => (
    Array.from({ length: 24 }, (_, i) => ({ label: `${i + 1} hours/day`, value: String(i + 1) }))
  ), []);

  const generateWorkingDaysArray = (numDays: number) => {
    return Array.from({ length: numDays }, (_, i) => i + 1);
  };

  const hasChanges = useMemo(() => {
    if (!initialValues) return false;

    return (
      workingDays !== initialValues.workingDays ||
      hoursPerDay !== initialValues.hoursPerDay ||
      onboardToggle !== initialValues.onboardToggle ||
      showCostToggle !== initialValues.showCostToggle ||
      storyPoints !== initialValues.storyPoints ||
      originalEstimate !== initialValues.originalEstimate ||
      selectedToleranceId !== initialValues.selectedToleranceId ||
      JSON.stringify(priorityLevels) !== JSON.stringify(initialValues.priorityLevels)
    );
  }, [workingDays, hoursPerDay, onboardToggle, showCostToggle, storyPoints, originalEstimate, selectedToleranceId, initialValues, priorityLevels]);

  const isWorkingScheduleReady = Boolean(initialValues);

  const handleSaveChanges = async () => {
    if (!org_id) return;

    const isAnyIntegrationActive = isAzureActive || isJiraActive;

    // Check if story points or original estimate changed before saving
    const storyPointsChanged = isAnyIntegrationActive && initialValues && Number(storyPoints) !== initialValues.storyPoints;
    const originalEstimateChanged = isAnyIntegrationActive && initialValues && Number(originalEstimate) !== initialValues.originalEstimate;

    try {
      const payload: any = {
        hours_per_day: hoursPerDay,
        working_days: generateWorkingDaysArray(workingDays),
        onboard_settings: {
          ...onboardSettings,
          close: !onboardToggle
        },
        show_cost: showCostToggle,
        priorities: priorityLevels,
        tolerance: selectedToleranceId
      };

      // Only include story_points and original_estimate if an integration is active
      if (isAnyIntegrationActive) {
        payload.story_points = Number(storyPoints) || 1;
        payload.original_estimate = Number(originalEstimate) || 8;
      }
      setSaveError(null);
      try{

        await updateOrganization(org_id, payload, true, false);
        
      }catch(error:any){
          const errorMsg = error?.response?.data?.detail || "Failed to save organization settings";
          setSaveError(errorMsg);

          if (initialValues?.priorityLevels) {
            setPriorityLevels(initialValues.priorityLevels);
          }

          return;
          // toast.error(errorMsg); // Optional: keep or remove toast
      }
    

      // Update initial values to reset hasChanges
      setInitialValues({
        workingDays,
        hoursPerDay,
        onboardToggle,
        showCostToggle,
        storyPoints: isAzureActive ? (Number(storyPoints) || 1) : initialValues?.storyPoints ?? 1,
        originalEstimate: isAzureActive ? (Number(originalEstimate) || 8) : initialValues?.originalEstimate ?? 8,
        selectedToleranceId: selectedToleranceId,
        priorityLevels: priorityLevels
      });

      // Show resync message if story points, original estimate or tolerance were changed
      const toleranceChanged = selectedToleranceId !== initialValues?.selectedToleranceId;
      if (storyPointsChanged || originalEstimateChanged || toleranceChanged) {
        setShowStoryPointsResyncMessage(storyPointsChanged ?? false);
        setShowOriginalEstimateResyncMessage(originalEstimateChanged ?? false);
        setShowToleranceResyncMessage(toleranceChanged);
      }

      toast.success("Organization settings saved successfully")
    } catch (error) {
      console.error('Failed to save organization settings:', error);
    }
  };

  const updateToleranceIndicator = useCallback(() => {
    const activeButton = typeof selectedToleranceId === 'number' ? toleranceButtonRefs.current[selectedToleranceId] : null;
    if (activeButton) {
      setToleranceIndicatorStyle({
        width: activeButton.offsetWidth,
        left: activeButton.offsetLeft,
      });
    }
  }, [selectedToleranceId]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      updateToleranceIndicator();
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedToleranceId, updateToleranceIndicator, toleranceModes]);

  useEffect(() => {
    window.addEventListener("resize", updateToleranceIndicator);
    return () => window.removeEventListener("resize", updateToleranceIndicator);
  }, [updateToleranceIndicator]);

  const handleOrgPreferencesClick = () => {
    getOrgPreferences(org_id);
    setPreferencesModalToggle(true);
  };

  const handleClosePreferencesModalClick = () => {
    setPreferencesModalToggle(false);
  };

  const handleDeleteAllClick = () => {
    setClearConfirmModal(false);
    deleteAllOrgPreferences(org_id);
  };

  const handleShowClearConfirm = () => {
    if (orgPreferences?.length) {
      setClearConfirmModal(true)
    }
  };

  const handleCopyOrgId = async () => {
    if (orgDetails?.org_id) {
      try {
        await navigator.clipboard.writeText(String(orgDetails.org_id));
        setCopiedText(true);
        setTimeout(() => setCopiedText(false), 2000);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-light-surfacePrimary dark:bg-tertiary-dark w-full">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-light-surfacePrimary dark:bg-tertiary-dark">
        <div className="mx-auto px-4 pt-8 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className=" border-b border-gray-200 dark:border-gray-700 pb-2">
            <div className="flex items-center gap-4 mb-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white font-['Inter']">
                  Organization Settings
                </h1>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 font-['Inter'] mt-1">
                  Configure your organization's working schedule and preferences
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="p-4 rounded-lg bg-white dark:bg-secondary-dark border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12">
                  {orgImageUrl ? (
                    <img
                      className="rounded-lg object-cover w-full h-full"
                      src={orgImageUrl}
                      alt={orgDetails?.name}
                    />
                  ) : (
                    <div className="bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                      <div className="w-8 h-8 bg-white bg-opacity-30 rounded dark:bg-opacity-20 flex items-center justify-center text-gray-800 dark:text-gray-300 font-bold text-lg">
                        {orgDetails?.name?.charAt(0)?.toUpperCase() || "O"}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white font-['Inter']">
                    {orgDetails?.name || "Organization Name"}
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600">
                      {orgDetails?.org_id || "Not available"}
                    </span>
                    {orgDetails?.org_id && (
                      <button
                        className="text-xs px-2.5 py-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors font-medium"
                        onClick={handleCopyOrgId}
                        title="Copy Organization ID"
                      >
                        {copiedText ? "Copied!" : "Copy"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Created {createdAtText}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-secondary-dark rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            {isWorkingScheduleReady ? (
              <div className="px-8 py-5 space-y-5">
                {/* Working Schedule Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white font-['Inter']">
                    Working Schedule
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Working Days */}
                    <div className="space-y-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            Working Days per Week
                          </div>
                          <CustomTooltip
                            title="Defines your organization's standard work week. A 5-day week excludes Saturdays and Sundays, while a 6-day week excludes only Sundays. This setting directly impacts timeline visualization, resource availability, and cost calculations."
                            placement="top"
                          >
                            <span className="cursor-help">
                              <HiInformationCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors" />
                            </span>
                          </CustomTooltip>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                          Select your organization's working week schedule
                        </p>
                      </div>
                      <SingleSelect
                        name="workingDays"
                        options={workingDaysOptions as any}
                        placeholder="Select working days"
                        value={
                          workingDaysOptions.find(
                            (o) => o.value === workingDays,
                          ) as any
                        }
                        onChange={(option: any) => {
                          const value =
                            typeof option?.value === "number"
                              ? option.value
                              : Number(option?.value);
                          if (!Number.isNaN(value)) setWorkingDays(value);
                        }}
                        className="text-sm"
                        width="w-full"
                      />
                    </div>

                    {/* Hours per Day */}
                    <div className="space-y-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            Daily Working Hours
                          </div>
                          <CustomTooltip
                            title="Specifies the number of productive hours in a standard work day. This conversion factor is used to calculate task duration on the timeline. For instance, with an 8-hour day, a 16-hour task will span 2 days, accurately reflecting task completion dates based on capacity."
                            placement="top"
                          >
                            <span className="cursor-help">
                              <HiInformationCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors" />
                            </span>
                          </CustomTooltip>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                          Standard working hours per day for capacity planning
                        </p>
                      </div>
                      <SingleSelect
                        name="hoursPerDay"
                        options={hoursOptions}
                        placeholder="Select hours per day"
                        value={hoursOptions.find(
                          (o) => Number(o.value) === hoursPerDay,
                        )}
                        onChange={(option) => {
                          const value = Number(option?.value);
                          if (!isNaN(value)) setHoursPerDay(value);
                        }}
                        className="text-sm"
                        width="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-gray-700"></div>

                {/* Help & Onboarding Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white font-['Inter']">
                      General Preferences
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700/50">
                      <ToggleSwitch
                        id="onboard"
                        name="onboard"
                        label=""
                        checked={onboardToggle}
                        onChange={(e) => {
                          setOnboardToggle(e.target.checked);
                        }}
                      />
                      <div className="space-y-1 flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          Onboarding Help
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                          Show guided tours and tooltips for new team members.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700/50">
                      <ToggleSwitch
                        id="showCost"
                        name="showCost"
                        label=""
                        checked={showCostToggle}
                        onChange={(e) => setShowCostToggle(e.target.checked)}
                      />
                      <div className="space-y-1 flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          Cost Visibility
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                          Enable cost data across the workspace. Only admins can
                          view costs.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700"></div>

                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white font-['Inter']">
                      Priority Levels
                    </h3>
                  </div>
                  <div className="mt-2">
                    <PriorityList
                      defaultData={priorityLevels}
                      onChange={(levels) => {
                        setPriorityLevels(levels);
                      }}
                    />
                  </div>
                  {/* Average Story Points - Only visible when Azure is active */}
                  {(isAzureActive || isJiraActive) && (
                    <>
                      <div className="border-t border-gray-200 dark:border-gray-700 mt-4"></div>
                      <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white font-['Inter']">
                            Integration Settings
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Average Story Points */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                Average Story Points
                              </div>
                              <CustomTooltip
                                title={`This value (${storyPoints}) is used as the default story points for items that don't have story points assigned in ${isAzureActive ? "Azure DevOps" : ""}${isAzureActive && isJiraActive ? " or " : ""}${isJiraActive ? "Jira" : ""}. It helps in capacity planning and workload estimation.`}
                                placement="top"
                              >
                                <span className="cursor-help">
                                  <HiInformationCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors" />
                                </span>
                              </CustomTooltip>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                              Default story points for tasks without assigned
                              values
                            </p>
                            <input
                              type="number"
                              id="storyPoints"
                              name="storyPoints"
                              min="1"
                              step="1"
                              value={storyPoints}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === "") {
                                  setStoryPoints("");
                                } else {
                                  const num = parseInt(val);
                                  // Only allow positive integers (greater than 0)
                                  if (!isNaN(num) && num > 0) {
                                    setStoryPoints(num);
                                  }
                                }
                              }}
                              onBlur={() => {
                                if (
                                  storyPoints === "" ||
                                  Number(storyPoints) < 1
                                ) {
                                  setStoryPoints(1);
                                }
                              }}
                              className="w-full h-[42px] px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 hover:border-blue-500"
                            />
                          </div>

                          {/* Average Original Estimate */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                Average Original Estimate
                              </div>
                              <CustomTooltip
                                title={`This value (${originalEstimate} hours) is used as the default original estimate for tasks that don't have original estimate assigned in ${isAzureActive ? "Azure DevOps" : ""}${isAzureActive && isJiraActive ? " or " : ""}${isJiraActive ? "Jira" : ""}. It helps in capacity planning and workload estimation.`}
                                placement="top"
                              >
                                <span className="cursor-help">
                                  <HiInformationCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors" />
                                </span>
                              </CustomTooltip>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                              Default original estimate (in hours) for tasks
                              without assigned values
                            </p>
                            <input
                              type="number"
                              id="originalEstimate"
                              name="originalEstimate"
                              min="1"
                              step="1"
                              value={originalEstimate}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === "") {
                                  setOriginalEstimate("");
                                } else {
                                  const num = parseInt(val);
                                  // Only allow positive integers (greater than 0)
                                  if (!isNaN(num) && num > 0) {
                                    setOriginalEstimate(num);
                                  }
                                }
                              }}
                              onBlur={() => {
                                if (
                                  originalEstimate === "" ||
                                  Number(originalEstimate) < 1
                                ) {
                                  setOriginalEstimate(8);
                                }
                              }}
                              className="w-full h-[42px] px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 hover:border-blue-500"
                            />
                          </div>
                        </div>

                        {/* Sync timing info - shows below inputs after story points or original estimate saved - FULL WIDTH */}
                        {(showStoryPointsResyncMessage ||
                          showOriginalEstimateResyncMessage) && (
                          <div className="mt-4">
                            <CacheResetAlert
                              title={
                                showStoryPointsResyncMessage && showOriginalEstimateResyncMessage
                                  ? "Story points and original estimate updated successfully!"
                                  : showStoryPointsResyncMessage
                                    ? "Story points updated successfully!"
                                    : "Original estimate updated successfully!"
                              }
                              description="This change will reflect to all your projects during the next auto-sync, which may take up to 24 hours."
                              buttonText="Resync Projects Now"
                              onClose={() => {
                                setShowStoryPointsResyncMessage(false);
                                setShowOriginalEstimateResyncMessage(false);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="border-t border-gray-200 dark:border-gray-700"></div>

                  <div className="space-y-4">
                    <div className="flex gap-2 flex-col">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white font-['Inter']">
                        Tolerance
                      </h3>
                      <p className=" text-xs text-gray-600 dark:text-gray-400 mt-[-10px]">
                        Lower tolerance prevents nearly completed work items
                        from triggering “At Risk” alerts for parent Features and
                        Epics.
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800/30 rounded-lg ">
                      {toleranceModesLoading ? (
                        <div className="animate-pulse space-y-4">
                          <div className="h-[42px] w-64 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                          <div className="space-y-2">
                            <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                            <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="relative inline-flex bg-white dark:bg-secondary-dark rounded-lg border border-gray-300 dark:border-gray-600 h-[42px] mb-4">
                            {/* Animated indicator */}
                            <div
                              className="absolute h-full bg-primary-light dark:bg-primary-dark rounded-lg transition-all duration-300 ease-in-out shadow-lg"
                              style={{
                                width: toleranceIndicatorStyle.width,
                                left: toleranceIndicatorStyle.left,
                                zIndex: 1,
                              }}
                            />

                            {toleranceModes.map((mode: any) => (
                              <button
                                key={mode.id}
                                ref={(el) => {
                                  toleranceButtonRefs.current[mode.id] = el;
                                }}
                                onClick={() => setSelectedToleranceId(mode.id)}
                                className={`relative px-6 h-full text-sm font-medium transition-colors duration-300 rounded-lg flex items-center gap-2 ${
                                  selectedToleranceId === mode.id
                                    ? "text-white"
                                    : "text-light-text-secondary dark:text-gray-400 hover:text-light-text-primary dark:hover:text-white"
                                }`}
                                style={{ zIndex: 2 }}
                              >
                                {mode.name.toLowerCase() === "chill" && (
                                  <HiEmojiHappy className="w-4 h-4" />
                                )}
                                {mode.name.toLowerCase() === "standard" && (
                                  <HiCheckCircle className="w-4 h-4" />
                                )}
                                {mode.name.toLowerCase() === "strict" && (
                                  <HiLockClosed className="w-4 h-4" />
                                )}
                                <span className="capitalize">{mode.name}</span>
                              </button>
                            ))}
                          </div>

                          {/* Display Description of Selected Mode */}
                          {selectedToleranceId !== null && (
                            <div className="animate-fade-in">
                              <p className="text-sm text-gray-600 dark:text-gray-300 italic">
                                {toleranceModes.find(
                                  (m: any) => m.id === selectedToleranceId,
                                )?.description || "No description available."}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {showToleranceResyncMessage && (
                      <div className="mt-4">
                        <CacheResetAlert
                          title="Tolerance level updated successfully!"
                          description="This change will reflect across all your work items within 24 hours during the next auto-sync."
                          buttonText="Refresh System Now"
                          onClose={() => setShowToleranceResyncMessage(false)}
                        />
                      </div>
                    )}
                  </div>

                
                </div>
              </div>
            ) : (
              <div
                className="px-8 py-5 space-y-6 animate-pulse"
                aria-hidden="true"
              >
                <div className="space-y-4">
                  <div className="h-5 w-48 rounded bg-gray-100 dark:bg-gray-700" />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="h-3 w-40 rounded bg-gray-100 dark:bg-gray-700" />
                      <div className="h-11 rounded-md bg-gray-100 dark:bg-gray-700" />
                    </div>
                    <div className="space-y-3">
                      <div className="h-3 w-40 rounded bg-gray-100 dark:bg-gray-700" />
                      <div className="h-11 rounded-md bg-gray-100 dark:bg-gray-700" />
                    </div>
                  </div>
                </div>
                <div className="border-t border-dashed border-gray-200 dark:border-gray-700" />
                <div className="space-y-4">
                  <div className="h-4 w-56 rounded bg-gray-100 dark:bg-gray-700" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="h-16 rounded-lg bg-gray-100 dark:bg-gray-700" />
                    <div className="h-16 rounded-lg bg-gray-100 dark:bg-gray-700" />
                  </div>
                </div>
              </div>
            )}

            {/* Error Alert */}
            {saveError && (
              <div className="px-8 pb-4">
                <Alert
                  color="failure"
                  icon={HiExclamation}
                  onDismiss={() => setSaveError(null)}
                  className="rounded-lg shadow-sm"
                >
                  <span className="font-medium text-sm">Error: </span>
                  <span className="text-sm">{saveError}</span>
                </Alert>
              </div>
            )}

            {/* Footer Actions */}
            <div className="px-8 py-3 bg-gray-50 dark:bg-gray-800/30 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-end items-center gap-3">
                <UnsavedChangesIndicator
                  hasUnsavedChanges={isWorkingScheduleReady && hasChanges}
                />
                <CustomButton
                  variant="primary"
                  onClick={handleSaveChanges}
                  disabled={
                    !isWorkingScheduleReady ||
                    loading ||
                    orgUpdateLoading ||
                    !hasChanges
                  }
                  className="!min-w-[120px]"
                >
                  {orgUpdateLoading ? "Saving..." : "Save Changes"}
                </CustomButton>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-secondary-dark rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            <div className="p-8">
              <div className="space-y-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="pr-4 flex-1">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white font-['Inter'] flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-blue-600 dark:text-blue-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                      Organization Preferences
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm font-['Inter'] leading-relaxed mt-2">
                      CadencePro will use your organization's domain knowledge
                      to become more helpful as you chat.
                    </p>
                  </div>
                  <CustomButton
                    onClick={handleOrgPreferencesClick}
                    variant="secondary"
                    className="whitespace-nowrap mt-1"
                  >
                    Manage
                  </CustomButton>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 font-['Inter']">
                    How to teach CadencePro about your organization:
                  </h4>
                  <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-5 border border-blue-100 dark:border-blue-800/30">
                    <ul className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                      <li className="flex gap-3 items-start">
                        <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full flex-shrink-0 mt-2"></div>
                        <span className="font-['Inter'] leading-relaxed">
                          We are Company XYZ, specializing in real estate
                          payment solutions and supporting clients across
                          multiple industries.
                        </span>
                      </li>
                      <li className="flex gap-3 items-start">
                        <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full flex-shrink-0 mt-2"></div>
                        <span className="font-['Inter'] leading-relaxed">
                          We deliver fixed-cost projects for software companies,
                          property managers, and others.
                        </span>
                      </li>
                      <li className="flex gap-3 items-start">
                        <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full flex-shrink-0 mt-2"></div>
                        <span className="font-['Inter'] leading-relaxed">
                          Our tech stack includes modern web and cloud
                          technologies such as Node.js, React, Python,
                          PostgreSQL, and AWS.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {preferencesModalToggle && (
          <OrgPreferencesModal
            onClose={handleClosePreferencesModalClick}
            onShowClearConfirm={handleShowClearConfirm}
          />
        )}

        {clearConfirmModalShow && (
          <ConfirmDeleteModal
            showModal={clearConfirmModalShow}
            setShowModal={setClearConfirmModal}
            handleConfirm={handleDeleteAllClick}
            isDeleting={orgPreferencesLoading}
            title="Clear CadencePro's memory"
            description={
              "This will permanently delete all organizational knowledge and preferences. Your AI assistant will lose all custom training data. This action cannot be undone."
            }
            confirmText="Delete"
          />
        )}
      </div>
    </div>
  );
};

export default OrgSettings;